import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  deleteCoverLetter,
  editCoverLetter,
  fetchCoverLetterData,
  fetchDefaultSettingsData,
  fetchSignedUrl,
  submitCoverLetter,
} from "../../../../../../services/selfAssessment.service";
import Button from "../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../components/button/types";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import { DragAndDropUploader } from "../../../../../../components/DragAndDropUploader";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as CloseIcon } from "../../../../../../assets/close.svg";
import CoverLetterTable from "./components/CoverLetters";
import DefaultSettingsTab from "./components/DefaultSettings";
import { ReactComponent as TypeWriter } from "../../../../../../assets/typewriter.svg";
import { DragAndDropUploaderModifiedForCoverLetterPopUp } from "../../../../../../components/DragAndDropUploader/DragAndDropModifiedForCoverLetterPopUp";
import Lottie from "lottie-react";
import typeWriterAnimation from "./../../../../../../assets/typewriter-animation.json";
import { extractTextFromPDF } from "./components/CheckPdfLenght";
import { MAX_CHARACTERS } from "../../utils";
import { MyAreaTitle } from "../../components";
interface CoverLetter {
  id: number;
  coverLetter: string;
  createdAt: string;
  formalityLevel: number;
  language: string;
  status: string;
  title: string;
}

interface CoverLetterData {
  coverLetters: CoverLetter[];
  count: number;
}

export function CoverLetterGenerator() {
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [jobAd, setJobAd] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const { t } = useTranslation("myArea");
  const { t: tcommon } = useTranslation("common");
  const { user } = useContext(AuthContext);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("cover-letter-tab");
  const [selectedOptionStep2, setSelectedOptionStep2] = useState<
    "upload-cv" | "default-cv"
  >("upload-cv");

  const [selectedOptionStep3, setSelectedOptionStep3] = useState<
    "scratch" | "template"
  >("scratch");

  const [selectedFormOfAddress, setSelectedFormOfAddress] = useState<
    "Du" | "Sie"
  >("Du");

  const [selectedTemplateProvider, setSelectedTemplateProvider] = useState<
    "new" | "default"
  >("new");

  const [useUploadedCVAsDefault, setUseUploadedCVAsDefault] =
    useState<boolean>(false);
  const [useUploadedTemplateAsDefault, setUseUploadedTemplateAsDefault] =
    useState<boolean>(false);

  const [language, setLanguage] = useState("German");
  const [formalityLevel, setFormalityLevel] = useState("business_casual");
  const [template, setTemplate] = useState("");
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const { data: coverLetters, refetch } = useQuery<CoverLetterData>({
    queryKey: ["coverLetter"],
    queryFn: fetchCoverLetterData,
    refetchInterval: 15000,
  });

  const { data: defaultSettings, refetch: refetchDefault } = useQuery({
    queryKey: ["defaultSettings"],
    queryFn: fetchDefaultSettingsData,
  });

  const handleCheckboxChangeCv = () => {
    setUseUploadedCVAsDefault((prev) => !prev);
  };

  const handleCheckboxChangeTemplate = () => {
    setUseUploadedTemplateAsDefault((prev) => !prev);
  };

  const submitMutation = useMutation({
    mutationFn: ({
      jobAd,
      cvFile,
      useDefaultCv,
      language,
      template,
      form,
      style,
      saveCvAsDefault,
      useDefaultTemplate,
      saveTemplateAsDefault,
    }: {
      jobAd: string;
      cvFile: File | null;
      useDefaultCv: boolean;
      language: string;
      template: string;
      form: string;
      style: string;
      saveCvAsDefault: boolean;
      useDefaultTemplate: boolean;
      saveTemplateAsDefault: boolean;
    }) =>
      submitCoverLetter({
        jobAd,
        cvFile,
        useDefaultCv,
        language,
        template,
        form,
        style,
        saveCvAsDefault,
        useDefaultTemplate,
        saveTemplateAsDefault,
      }),
  });

  const handleNext = () => {
    if (!jobAd.trim()) {
      alert("Please enter a Job Ad description.");
      return;
    }
    setStep(2);
  };

  const tabs = useMemo(() => {
    const baseTabs = ["cover-letter-tab", "default-settings-tab"];
    return baseTabs;
  }, []);

  const handleDownload = async () => {
    try {
      const url = await fetchSignedUrl(defaultSettings?.cvFile?.awsKey);

      setTimeout(() => {
        window.open(url, "_blank");
      });
    } catch (error) {
      console.error("Error fetching signed URL:", error);
    }
  };

  const handleSubmit = () => {
    setStep(4);
    setIsSubmitting(true); // Prevent multiple clicks

    submitMutation.mutate(
      {
        jobAd,
        cvFile,
        useDefaultCv: selectedOptionStep2 === "default-cv",
        language: language,
        template: template,
        form: selectedFormOfAddress,
        style: formalityLevel,
        saveCvAsDefault: useUploadedCVAsDefault,
        useDefaultTemplate: selectedTemplateProvider === "default",
        saveTemplateAsDefault: useUploadedTemplateAsDefault,
      },
      {
        onSuccess: () => {
          setMessage(`${t("assessments.cover-letter.success")}`);
          setIsError(false);

          // Wait 2 seconds before closing
          setTimeout(() => {
            setIsOpen(false);
            clearFieldsAndErrors();
            setMessage(null);
            setIsSubmitting(false);
            setIsError(false);

            refetch();
            refetchDefault();

            setActiveTab("cover-letter-tab");
            if (coverLetters) {
              setExpandedRow(0);
            }
          }, 2000);
        },
        onError: () => {
          setMessage(`${t("assessments.cover-letter.failure")}`);
          setIsError(true);
          setIsSubmitting(false);

          // Keep message visible for 2 seconds
          setTimeout(() => {
            setMessage(null);
            setIsOpen(false);
            clearFieldsAndErrors();
          }, 3000);
        },
      }
    );
  };

  const clearFieldsAndErrors = () => {
    setCvFile(null);
    setJobAd("");
    setFileError(null);
    setStep(1);
    setSelectedOptionStep2("upload-cv");
    setSelectedOptionStep3("scratch");
    setSelectedFormOfAddress("Du");
    setSelectedTemplateProvider("new");
    setUseUploadedCVAsDefault(false);
    setUseUploadedTemplateAsDefault(false);
    setLanguage("German");
    setFormalityLevel("business_casual");
    setTemplate("");
    setMessage(null);
    setIsError(false);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    clearFieldsAndErrors();
  };

  useEffect(() => {
    if (cvFile && !defaultSettings?.cvFile) {
      setUseUploadedCVAsDefault(true);
    } else {
      setUseUploadedCVAsDefault(false);
    }
  }, [cvFile, defaultSettings]);

  useEffect(() => {
    if (selectedOptionStep3 === "template" && !defaultSettings?.template) {
      setUseUploadedTemplateAsDefault(true);
    } else {
      setUseUploadedTemplateAsDefault(false);
    }
  }, [selectedOptionStep3, defaultSettings]);

  useEffect(() => {
    if (selectedTemplateProvider === "default") {
      setTemplate("");
    }
  }, [selectedTemplateProvider]);

  const isOverLimit = jobAd.length > MAX_CHARACTERS;

  useEffect(() => {
    const storedJobData = localStorage.getItem("coverLetterJobAd");

    if (storedJobData) {
      const parsedData = JSON.parse(storedJobData);

      const formattedJobAd = `${parsedData.jobDescription}`;

      setJobAd(formattedJobAd);

      if (parsedData.autoOpen) {
        setIsOpen(true);
      }

      localStorage.removeItem("coverLetterJobAd");
    }
  }, []);

  return (
    <>
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("assessments.title-cover-letter")} />
      </div>
      <div className="mb-6 ">
        <span>{t(`assessments.cover-letter.explanatory-text`)}</span>
      </div>
      <div className="flex justify-end">
        {" "}
        <Button
          className="mb-2"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={() => setIsOpen(true)}
          disabled={coverLetters && coverLetters.count >= 100}
        >
          {t("assessments.cover-letter.button")}
        </Button>
      </div>
      {coverLetters && coverLetters.count >= 100 && (
        <div className="flex justify-end text-[#757575] mr-2">
          <InfoIcon className="mr-2" /> {t("assessments.cover-letter.limit")}
        </div>
      )}

      {coverLetters && coverLetters?.coverLetters?.length > 0 && (
        <>
          <div className="flex w-full">
            {tabs
              .filter(
                (tab) =>
                  tab !== "default-settings-tab" ||
                  defaultSettings?.cvFile ||
                  defaultSettings?.template
              )
              .map((tab) => (
                <div
                  key={tab}
                  className={`pt-6 pb-0 pl-0 pr-4 cursor-pointer ${
                    activeTab === tab ? "font-bold" : ""
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  <span className="p-2 pb-0 mb-2 flex">
                    {t(`assessments.cover-letter.${tab}`)}
                  </span>
                  {activeTab === tab && (
                    <div className="h-[2px] bg-Yellow-default w-full" />
                  )}
                </div>
              ))}
          </div>
          <hr />
          {activeTab === "cover-letter-tab" && (
            <>
              <CoverLetterTable
                coverLetters={coverLetters?.coverLetters}
                refetch={refetch}
                setExpandedRow={setExpandedRow}
                expandedRow={expandedRow}
              />
            </>
          )}

          {activeTab === "default-settings-tab" &&
            (defaultSettings?.cvFile || defaultSettings?.template) && (
              <DefaultSettingsTab />
            )}
        </>
      )}
      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-6 lg:rounded-lg shadow-lg lg:w-1/3 w-full h-full lg:h-auto">
            {step === 1 ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">
                    {" "}
                    {t("assessments.cover-letter.step1-title")}
                  </span>
                  <CloseIcon
                    onClick={() => {
                      setIsOpen(false);
                      clearFieldsAndErrors();
                    }}
                  />
                </div>

                <h3>{t("assessments.cover-letter.step1-description")}</h3>

                <textarea
                  placeholder={
                    t("assessments.cover-letter.step1-placeholder") as string
                  }
                  value={jobAd}
                  onChange={(e) => setJobAd(e.target.value)}
                  rows={6}
                  className="mb-4 mt-4 border p-2 w-full resize-none rounded-md"
                />

                {isOverLimit && (
                  <p className="text-red-900 text-sm">
                    {tcommon("character-limit-error", {
                      max: MAX_CHARACTERS,
                    })}
                  </p>
                )}

                {/* Buttons */}
                <div
                  className="w-full py-3 px-4  lg:py-0 lg:px-0 
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={handleCancel}
                    >
                      {t("assessments.cover-letter.popup-cancel")}
                    </Button>
                    <Button
                      className=" w-full ml-2"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={handleNext}
                      disabled={!jobAd.trim() || isOverLimit}
                    >
                      {t("assessments.cover-letter.button-next")}
                    </Button>
                  </div>
                </div>
              </>
            ) : step === 2 ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">
                    {" "}
                    {t("assessments.cover-letter.step2-title")}
                  </span>
                  <CloseIcon
                    onClick={() => {
                      setIsOpen(false);
                      clearFieldsAndErrors();
                    }}
                  />
                </div>

                <h3>{t("assessments.cover-letter.step2-description")}</h3>

                <div className="mt-4 flex flex-col">
                  {/* Radio Button: Upload CV (Drag and Drop) */}
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="cvOption"
                      value="upload-cv"
                      checked={selectedOptionStep2 === "upload-cv"}
                      onChange={() => setSelectedOptionStep2("upload-cv")}
                      className="hidden"
                    />
                    <div
                      className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                        selectedOptionStep2 === "upload-cv"
                          ? "border-Yellow-default"
                          : "border-Yellow-default"
                      }`}
                      style={{ aspectRatio: "1" }}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          selectedOptionStep2 === "upload-cv"
                            ? "bg-Yellow-default"
                            : "bg-white"
                        } transition-all`}
                      />
                    </div>
                    <span
                      className={`ml-2 ${
                        selectedOptionStep2 === "upload-cv" ? "font-bold" : ""
                      }`}
                    >
                      {t("assessments.cover-letter.step2-option1")}
                    </span>
                  </label>

                  {selectedOptionStep2 === "upload-cv" && (
                    <>
                      <div className="ml-7">
                        <DragAndDropUploaderModifiedForCoverLetterPopUp
                          singleFile
                          filesSetter={(files) => {
                            if (files.length > 0) {
                              const file = files[0];

                              if (file.size > 4 * 1024 * 1024) {
                                setFileError("File size exceeds 2MB");
                                setCvFile(null);
                              } else {
                                setCvFile(file);
                                setFileError(null);
                              }
                            } else {
                              setCvFile(null);
                            }
                          }}
                          currentFile={cvFile}
                        />
                        {fileError && (
                          <p className="text-sm text-red-900">{fileError}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {cvFile && (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={useUploadedCVAsDefault}
                        onChange={handleCheckboxChangeCv}
                        className="hidden"
                      />
                      {/* Custom checkbox container */}
                      <div
                        className={`w-5 h-5 mr-2 ml-7 border-2 rounded-[4px] flex items-center justify-center relative ${
                          useUploadedCVAsDefault
                            ? "border-[#757575]"
                            : "border-[#757575]"
                        }`}
                      >
                        {/* Inner filled circle */}
                        <div
                          className={`w-4 h-4 rounded-[4px] ${
                            useUploadedCVAsDefault
                              ? "border-[#757575] bg-Yellow-default"
                              : "border-[#757575]"
                          }`}
                        />
                      </div>
                      <span className=" text-[#757575]">
                        {" "}
                        {t("assessments.cover-letter.save-this-as-default")}
                      </span>
                    </label>
                  </div>
                )}
                {defaultSettings?.cvFile && (
                  <div className="mt-4">
                    {/* Radio Button: Use Default CV */}
                    <label className="flex items-start">
                      <input
                        type="radio"
                        name="cvOption"
                        value="default-cv"
                        checked={selectedOptionStep2 === "default-cv"}
                        onChange={() => {
                          setSelectedOptionStep2("default-cv");
                          setCvFile(null);
                        }}
                        className="hidden"
                      />
                      <div
                        className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                          selectedOptionStep2 === "default-cv"
                            ? "border-Yellow-default"
                            : "border-Yellow-default"
                        }`}
                        style={{ aspectRatio: "1" }}
                      >
                        <div
                          className={`w-3 h-3 rounded-full ${
                            selectedOptionStep2 === "default-cv"
                              ? "bg-Yellow-default"
                              : "bg-white"
                          } transition-all`}
                        />
                      </div>

                      <span
                        className={`ml-2 ${
                          selectedOptionStep2 === "default-cv"
                            ? "font-bold"
                            : ""
                        }`}
                      >
                        {t("assessments.cover-letter.step2-option2")}
                      </span>
                    </label>
                    {defaultSettings?.cvFile && (
                      <div className="flex justify-start mt-1 mb-4 text-[#757575] cursor-pointer">
                        <span
                          className="ml-6 w-full break-words overflow-wrap break-all"
                          onClick={handleDownload}
                        >
                          {defaultSettings.cvFile.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {fileError && (
                  <p className="text-sm text-red-900">{fileError}</p>
                )}

                <div
                  className="w-full py-3 px-4  lg:py-0 lg:px-0 
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between lg:mt-4">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(1)}
                    >
                      {t("assessments.cover-letter.popup-back")}
                    </Button>
                    <Button
                      type="button"
                      className="w-full ml-2"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(3)}
                      disabled={
                        (!cvFile || fileError !== null) &&
                        selectedOptionStep2 === "upload-cv"
                      }
                    >
                      {t("assessments.cover-letter.button-next")}
                    </Button>
                  </div>
                </div>
              </>
            ) : step === 3 ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">
                    {" "}
                    {t("assessments.cover-letter.step3-title")}
                  </span>
                  <CloseIcon
                    onClick={() => {
                      setIsOpen(false);
                      clearFieldsAndErrors();
                    }}
                  />
                </div>

                <h3>{t("assessments.cover-letter.step3-description")}</h3>

                <div className="mt-4">
                  {/* Radio Button: Scratch, template */}
                  <label className="flex items-start mb-2">
                    <input
                      type="radio"
                      name="cvOption"
                      value="upload-cv"
                      checked={selectedOptionStep3 === "scratch"}
                      onChange={() => setSelectedOptionStep3("scratch")}
                      className="hidden"
                    />
                    <div
                      className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                        selectedOptionStep3 === "scratch"
                          ? "border-Yellow-default"
                          : "border-Yellow-default"
                      }`}
                      style={{ aspectRatio: "1" }}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          selectedOptionStep3 === "scratch"
                            ? "bg-Yellow-default"
                            : "bg-white"
                        } transition-all`}
                      />
                    </div>
                    <span
                      className={`ml-2 ${
                        selectedOptionStep3 === "scratch" ? "font-bold" : ""
                      }`}
                    >
                      {t("assessments.cover-letter.step3-option1")}
                    </span>
                  </label>

                  {selectedOptionStep3 === "scratch" && (
                    <>
                      <span className={`ml-7`}>
                        {t("assessments.cover-letter.step3-tone")}
                      </span>
                      <select
                        value={formalityLevel}
                        onChange={(e) => setFormalityLevel(e.target.value)}
                        className="w-[95%] p-2 mt-1 ml-6 mr-6 border rounded-md ml-7 mb-4"
                      >
                        <option value="business_casual">
                          {t("assessments.cover-letter.formality-business")}
                        </option>
                        <option value="creative">
                          {t("assessments.cover-letter.formality-creative")}
                        </option>
                        <option value="formal">
                          {t("assessments.cover-letter.formality-formal")}
                        </option>
                      </select>

                      <span className={`ml-7 `}>
                        {t("assessments.cover-letter.step3-language")}:
                      </span>

                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-[95%] p-2 ml-7 mt-1 mb-2 mr-6 border rounded-md"
                      >
                        <option value="English">
                          {t("assessments.cover-letter.language-english")}
                        </option>
                        <option value="German">
                          {t("assessments.cover-letter.language-german")}
                        </option>
                      </select>

                      {language === "German" && formalityLevel !== "formal" && (
                        <>
                          <div className="mb-3">
                            <span className={`ml-7`}>
                              {t("assessments.cover-letter.step3-form")}
                            </span>

                            <label className="flex items-start">
                              <input
                                type="radio"
                                name="cvOption"
                                value="upload-cv"
                                checked={selectedFormOfAddress === "Du"}
                                onChange={() => setSelectedFormOfAddress("Du")}
                                className="hidden"
                              />
                              <div
                                className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ml-7 ${
                                  selectedFormOfAddress === "Du"
                                    ? "border-Yellow-default"
                                    : "border-Yellow-default"
                                }`}
                                style={{ aspectRatio: "1" }}
                              >
                                <div
                                  className={`w-3 h-3 rounded-full ${
                                    selectedFormOfAddress === "Du"
                                      ? "bg-Yellow-default"
                                      : "bg-white"
                                  } transition-all`}
                                />
                              </div>
                              <span>
                                <span
                                  className={`ml-2 ${
                                    selectedFormOfAddress === "Du"
                                      ? "font-bold"
                                      : ""
                                  }`}
                                >
                                  {t("assessments.cover-letter.step3-du")}
                                </span>

                                <span className="text-[#A7B6C5]">
                                  {""} (
                                  {t(
                                    "assessments.cover-letter.step3-du-explain"
                                  )}
                                  )
                                </span>
                              </span>
                            </label>

                            <label className="flex items-start mt-1">
                              <input
                                type="radio"
                                name="cvOption"
                                value="upload-cv"
                                checked={selectedFormOfAddress === "Sie"}
                                onChange={() => setSelectedFormOfAddress("Sie")}
                                className="hidden"
                              />
                              <div
                                className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ml-7 ${
                                  selectedFormOfAddress === "Sie"
                                    ? "border-Yellow-default"
                                    : "border-Yellow-default"
                                }`}
                                style={{ aspectRatio: "1" }}
                              >
                                <div
                                  className={`w-3 h-3 rounded-full ${
                                    selectedFormOfAddress === "Sie"
                                      ? "bg-Yellow-default"
                                      : "bg-white"
                                  } transition-all`}
                                />
                              </div>
                              <span>
                                <span
                                  className={`ml-2 ${
                                    selectedFormOfAddress === "Sie"
                                      ? "font-bold"
                                      : ""
                                  }`}
                                >
                                  {t("assessments.cover-letter.step3-sie")}
                                </span>
                                <span className="text-[#A7B6C5]">
                                  {""} (
                                  {t(
                                    "assessments.cover-letter.step3-sie-explain"
                                  )}
                                  )
                                </span>
                              </span>
                            </label>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-4">
                  {/* Radio Button: Use Default CV */}
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="cvOption"
                      value="default-cv"
                      checked={selectedOptionStep3 === "template"}
                      onChange={() => {
                        setSelectedOptionStep3("template");
                      }}
                      className="hidden"
                    />
                    <div
                      className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                        selectedOptionStep3 === "template"
                          ? "border-Yellow-default"
                          : "border-Yellow-default"
                      }`}
                      style={{ aspectRatio: "1" }}
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${
                          selectedOptionStep3 === "template"
                            ? "bg-Yellow-default"
                            : "bg-white"
                        } transition-all`}
                      />
                    </div>

                    <span
                      className={`ml-2 ${
                        selectedOptionStep3 === "template" ? "font-bold" : ""
                      }`}
                    >
                      {t("assessments.cover-letter.step3-option2")}
                    </span>
                  </label>

                  {selectedOptionStep3 === "template" && (
                    <>
                      {" "}
                      <label className="flex items-start ml-7 mt-2 mb-2">
                        <input
                          type="radio"
                          name="templateOption"
                          value="new"
                          checked={selectedTemplateProvider === "new"}
                          onChange={() => {
                            setSelectedTemplateProvider("new");
                          }}
                          className="hidden"
                        />
                        <div
                          className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                            selectedTemplateProvider === "new"
                              ? "border-Yellow-default"
                              : "border-Yellow-default"
                          }`}
                          style={{ aspectRatio: "1" }}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${
                              selectedTemplateProvider === "new"
                                ? "bg-Yellow-default"
                                : "bg-white"
                            } transition-all`}
                          />
                        </div>

                        <span
                          className={`ml-2 ${
                            selectedTemplateProvider === "new"
                              ? "font-bold"
                              : ""
                          }`}
                        >
                          {t(
                            "assessments.cover-letter.step3-template-provider"
                          )}
                        </span>
                      </label>
                      {selectedTemplateProvider === "new" && (
                        <>
                          <div className="flex items-center justify-between ml-14">
                            <textarea
                              placeholder={
                                t(
                                  "assessments.cover-letter.step3-placeholder"
                                ) as string
                              }
                              value={template}
                              onChange={(e) => setTemplate(e.target.value)}
                              rows={6}
                              className="mb-4 mt-2 border p-2 w-full resize-none rounded-md"
                            />
                          </div>{" "}
                        </>
                      )}
                      {template && selectedTemplateProvider === "new" && (
                        <div className="ml-7 mb-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={useUploadedTemplateAsDefault}
                              onChange={handleCheckboxChangeTemplate}
                              className="hidden"
                            />
                            {/* Custom checkbox container */}
                            <div
                              className={`w-5 h-5 mr-2 ml-7 border-2 rounded-[4px] flex items-center justify-center relative ${
                                useUploadedTemplateAsDefault
                                  ? "border-[#757575]"
                                  : "border-[#757575]"
                              }`}
                            >
                              {/* Inner filled circle */}
                              <div
                                className={`w-4 h-4 rounded-[4px] ${
                                  useUploadedTemplateAsDefault
                                    ? "border-[#757575] bg-Yellow-default"
                                    : "border-[#757575]"
                                }`}
                              />
                            </div>
                            <span className=" text-[#757575]">
                              {" "}
                              {t(
                                "assessments.cover-letter.save-this-as-default-template"
                              )}
                            </span>
                          </label>
                        </div>
                      )}
                      {defaultSettings?.template && (
                        <>
                          <label className="flex items-start ml-7 ">
                            <input
                              type="radio"
                              name="templateOption"
                              value="default"
                              checked={selectedTemplateProvider === "default"}
                              onChange={() => {
                                setSelectedTemplateProvider("default");
                                setTemplate("");
                              }}
                              className="hidden"
                            />
                            <div
                              className={`w-5 h-5 border-2 rounded-full flex items-center justify-center ${
                                selectedTemplateProvider === "default"
                                  ? "border-Yellow-default"
                                  : "border-Yellow-default"
                              }`}
                              style={{ aspectRatio: "1" }}
                            >
                              <div
                                className={`w-3 h-3 rounded-full ${
                                  selectedTemplateProvider === "default"
                                    ? "bg-Yellow-default"
                                    : "bg-white"
                                } transition-all`}
                              />
                            </div>

                            <span
                              className={`ml-2 ${
                                selectedTemplateProvider === "default"
                                  ? "font-bold"
                                  : ""
                              }`}
                            >
                              {t(
                                "assessments.cover-letter.step3-template-provider-default"
                              )}
                            </span>
                          </label>
                          {selectedTemplateProvider === "default" && (
                            <>
                              <div className="flex justify-start mt-1 mb-4 text-[#757575] ml-14">
                                <textarea
                                  placeholder={
                                    t(
                                      "assessments.cover-letter.cover-letter-template-placeholder"
                                    ) as string
                                  }
                                  value={defaultSettings?.template}
                                  rows={5}
                                  className="mb-4 mt-2 border p-2 w-full resize-none rounded-md"
                                  readOnly={true}
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                <div
                  className="w-full py-3 px-4  lg:py-0 lg:px-0 
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(2)}
                    >
                      {t("assessments.cover-letter.popup-back")}
                    </Button>
                    <Button
                      type="button"
                      className="w-full ml-2"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {t("assessments.cover-letter.popup-submit")}
                    </Button>
                  </div>{" "}
                </div>
              </>
            ) : step === 4 ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">
                    {" "}
                    {t("assessments.cover-letter.final-title")}
                  </span>
                  <CloseIcon
                    onClick={() => {
                      setIsOpen(false);
                      clearFieldsAndErrors();
                    }}
                  />
                </div>

                <h3>{t("assessments.cover-letter.final-description")}</h3>
                <div className="flex items-center justify-center mt-16 mb-16">
                  {isSubmitting && (
                    <Lottie
                      animationData={typeWriterAnimation}
                      loop={true}
                      style={{ width: "146px", height: "146px" }}
                    />
                  )}
                </div>
                {message && (
                  <p
                    className={`mt-4 text-center text-lg ${
                      isError ? "text-red-900" : "text-green-900"
                    }`}
                  >
                    {message}
                  </p>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
